import React, { useState, useCallback, useEffect, useRef } from 'react'
import Cropper from 'react-easy-crop'
import 'react-easy-crop/react-easy-crop.css'
import { Button } from '@t-one-internal/design-system'
import { ArrowClockwise, ArrowCounterClockwise } from '@phosphor-icons/react'
import type { Area, Point } from 'react-easy-crop'

interface LogoEditorProps {
  imageSrc: string
  onSave: (croppedImageBlob: Blob) => void
  onCancel: () => void
  format: 'square' | 'rectangle'
  onFormatChange?: (format: 'square' | 'rectangle') => void
  companyName?: string
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', (error) => reject(error))
    image.src = url
  })

const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: Area,
  format: 'square' | 'rectangle',
  rotation: number = 0,
  quality: number = 0.85,
  maxSize?: { width: number; height: number }
): Promise<Blob> => {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    throw new Error('No 2d context')
  }

  // Calculate rotation in radians
  const rad = (rotation * Math.PI) / 180

  // Set canvas size based on format
  let outputWidth: number
  let outputHeight: number

  if (format === 'square') {
    // Square crop (1:1) - max 400x400 for compression
    const size = Math.min(pixelCrop.width, pixelCrop.height)
    outputWidth = size
    outputHeight = size
  } else {
    // Rectangle crop (3.5:1) - max 1400x400 for compression
    outputWidth = pixelCrop.width
    outputHeight = pixelCrop.height
  }

  // Apply max size constraints if provided (for compression)
  if (maxSize) {
    const aspectRatio = outputWidth / outputHeight
    if (outputWidth > maxSize.width || outputHeight > maxSize.height) {
      if (outputWidth > outputHeight) {
        outputWidth = maxSize.width
        outputHeight = outputWidth / aspectRatio
      } else {
        outputHeight = maxSize.height
        outputWidth = outputHeight * aspectRatio
      }
    }
  }

  canvas.width = outputWidth
  canvas.height = outputHeight

  // Handle rotation properly
  // react-easy-crop provides pixelCrop coordinates in the rotated image space
  // We need to rotate the source image first, then crop from the rotated image
  if (rotation !== 0) {
    // Create a temporary canvas to rotate the image
    const tempCanvas = document.createElement('canvas')
    const tempCtx = tempCanvas.getContext('2d')
    
    if (!tempCtx) {
      throw new Error('No 2d context for temp canvas')
    }

    // Calculate dimensions after rotation
    const sin = Math.abs(Math.sin(rad))
    const cos = Math.abs(Math.cos(rad))
    const rotatedWidth = image.width * cos + image.height * sin
    const rotatedHeight = image.width * sin + image.height * cos

    tempCanvas.width = rotatedWidth
    tempCanvas.height = rotatedHeight

    // Clear and rotate the image
    tempCtx.fillStyle = '#FFFFFF'
    tempCtx.fillRect(0, 0, rotatedWidth, rotatedHeight)
    tempCtx.translate(rotatedWidth / 2, rotatedHeight / 2)
    tempCtx.rotate(rad)
    tempCtx.translate(-image.width / 2, -image.height / 2)
    tempCtx.drawImage(image, 0, 0)

    // Now crop from the rotated image
    // pixelCrop coordinates are already in the rotated image space
    ctx.drawImage(
      tempCanvas,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      outputWidth,
      outputHeight
    )
  } else {
    // No rotation - direct crop
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      outputWidth,
      outputHeight
    )
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'))
          return
        }
        resolve(blob)
      },
      'image/jpeg',
      quality
    )
  })
}

export const LogoEditor: React.FC<LogoEditorProps> = ({
  imageSrc,
  onSave,
  onCancel,
  format,
  onFormatChange,
  companyName,
}) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  // Use a data URL for preview instead of a blob: URL to avoid creating many blob:// entries
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const previewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [currentFormat, setCurrentFormat] = useState<'square' | 'rectangle'>(format)

  const aspectRatio = currentFormat === 'square' ? 1 : 3.5 / 1

  // Update current format when prop changes
  useEffect(() => {
    setCurrentFormat(format)
  }, [format])

  const handleFormatToggle = (newFormat: 'square' | 'rectangle') => {
    setCurrentFormat(newFormat)
    if (onFormatChange) {
      onFormatChange(newFormat)
    }
  }

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels)
    },
    []
  )

  // Real-time preview update (uses data URLs, not blob: URLs)
  const updatePreview = useCallback(async () => {
    if (!croppedAreaPixels) return

    // Clear previous timeout
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current)
    }

    // Debounce preview updates for performance
    previewTimeoutRef.current = setTimeout(async () => {
      try {
        // Use lower quality and smaller size for preview to save resources
        const previewMaxSize = currentFormat === 'square' 
          ? { width: 200, height: 200 }
          : { width: 700, height: 200 }
        const croppedBlob = await getCroppedImg(
          imageSrc, 
          croppedAreaPixels, 
          currentFormat, 
          rotation,
          0.7, // Lower quality for preview
          previewMaxSize // Smaller size for preview
        )

        // Convert blob to a data URL so we don't create blob:// object URLs repeatedly
        const reader = new FileReader()
        reader.onloadend = () => {
          const url = reader.result as string
          if (url) {
            setPreviewUrl(url)
          }
        }
        reader.readAsDataURL(croppedBlob)
      } catch (error) {
        console.error('Error generating preview:', error)
      }
    }, 150) // 150ms debounce
  }, [croppedAreaPixels, currentFormat, rotation, imageSrc, previewUrl])

  // Update preview whenever crop, zoom, rotation, or format changes
  useEffect(() => {
    updatePreview()
    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current)
      }
    }
  }, [crop, zoom, rotation, croppedAreaPixels, currentFormat, updatePreview])

  const handleRotate = (direction: 'left' | 'right') => {
    setRotation((prev) => (direction === 'right' ? prev + 90 : prev - 90))
  }

  const handleSave = async () => {
    if (!croppedAreaPixels) return

    setIsSaving(true)
    try {
      // Use higher quality and appropriate max size for final image
      const maxSize = currentFormat === 'square'
        ? { width: 400, height: 400 } // Square: 400x400 max
        : { width: 1400, height: 400 } // Rectangle: 1400x400 max (3.5:1)
      
      const croppedBlob = await getCroppedImg(
        imageSrc, 
        croppedAreaPixels, 
        currentFormat, 
        rotation,
        0.85, // Higher quality for final image
        maxSize // Compress to max dimensions
      )
      onSave(croppedBlob)
    } catch (error) {
      console.error('Error cropping image:', error)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="flex gap-6 relative">
      {/* Left Side - Cropper and Controls */}
      <div className="flex-1 flex flex-col gap-4">
        {/* Cropper Container */}
        <div className="relative w-full" style={{ height: '500px', background: '#f3f4f6', borderRadius: '8px' }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspectRatio}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
            cropShape="rect"
            showGrid={true}
          />
        </div>

        {/* Controls - Sticky positioned to stay visible when scrolling */}
        <div className="space-y-4 sticky top-4 bg-white z-10 pb-2 shadow-sm rounded-lg p-4 border border-gray-100">
          {/* Format Toggle */}
          <div className="space-y-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <p className="text-sm font-medium text-gray-700 mb-3">
              Choose logo format:
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleFormatToggle('square')}
                className={`p-4 border-2 rounded-lg transition-colors text-left ${
                  currentFormat === 'square'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                }`}
              >
                <div className="font-medium text-gray-900 mb-1">Square</div>
                <div className="text-xs text-gray-600">1:1 ratio • Shows with company name</div>
              </button>
              <button
                onClick={() => handleFormatToggle('rectangle')}
                className={`p-4 border-2 rounded-lg transition-colors text-left ${
                  currentFormat === 'rectangle'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                }`}
              >
                <div className="font-medium text-gray-900 mb-1">Rectangle</div>
                <div className="text-xs text-gray-600">3.5:1 ratio • Landscape • No text</div>
              </button>
            </div>
          </div>

          {/* Zoom Slider */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Zoom</span>
              <span className="text-sm font-medium text-slate-900">
                {Math.round(zoom * 100)}%
              </span>
            </div>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>

          {/* Rotation Controls */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600">Rotate:</span>
            <button
              onClick={() => handleRotate('left')}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              title="Rotate left"
            >
              <ArrowCounterClockwise className="w-5 h-5 text-gray-700" />
            </button>
            <span className="text-sm font-medium text-slate-900 min-w-[3rem] text-center">
              {rotation}°
            </span>
            <button
              onClick={() => handleRotate('right')}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              title="Rotate right"
            >
              <ArrowClockwise className="w-5 h-5 text-gray-700" />
            </button>
          </div>

          {/* Instructions */}
          <p className="text-xs text-slate-500 text-center">
            {currentFormat === 'square'
              ? 'Drag to position • Adjust zoom • Rotate if needed'
              : 'Drag to position • Adjust zoom • Rotate if needed • Aspect ratio: 3.5:1'}
          </p>

          {/* Action Buttons - Always visible at bottom of controls section */}
          <div className="flex gap-2 pt-2 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isSaving}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || !croppedAreaPixels}
              className="flex-1"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      {/* Right Side - Fixed Preview */}
      <div className="w-80 flex-shrink-0">
        <div className="sticky top-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
          <p className="text-sm font-medium text-gray-700 mb-4">Live Preview:</p>
          {previewUrl ? (
            currentFormat === 'square' ? (
              // Square preview with company name
              <div className="flex items-center gap-3 px-4 py-6 bg-white rounded-lg border border-gray-200 shadow-sm">
                <img
                  src={previewUrl}
                  alt="Logo Preview"
                  className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
                />
                <span
                  className="text-xl font-semibold text-gray-900"
                  style={{
                    lineHeight: '1.3',
                    wordBreak: 'break-word',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    flex: 1,
                  }}
                >
                  {companyName || 'Company Name'}
                </span>
              </div>
            ) : (
              // Rectangle preview (no text)
              <div className="flex items-center justify-center bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                <img
                  src={previewUrl}
                  alt="Logo Preview"
                  className="w-full h-auto rounded-lg object-contain"
                  style={{ maxHeight: '80px' }}
                />
              </div>
            )
          ) : (
            <div className="flex items-center justify-center bg-white rounded-lg border border-gray-200 p-8">
              <p className="text-sm text-gray-400">Adjust image to see preview</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

